'use client'

import { useEffect, useState } from 'react'
import {
  decemberUtcOffsetHours,
  resolveTimeZoneName,
  type ViewerClock,
} from '@/lib/santa-tracker'

export interface TrackerClock extends ViewerClock {
  /** Nombre IANA de la zona del visitante; `null` hasta que el cliente monta. */
  timeZoneName: string | null
}

/**
 * El reloj vivo de la página.
 *
 * Arranca con el instante y el desfase que **pasó el servidor** y solo tras
 * montar cambia a los del dispositivo. Ese orden no es un detalle: todo lo que
 * se pinta depende de la hora, así que si el primer render del cliente no
 * coincidiera con el HTML del servidor, React descartaría el árbol entero.
 *
 * `timeZoneName` empieza en `null` a propósito: el HTML indexable no puede
 * afirmar una zona horaria que todavía no conoce.
 */
export function useTrackerClock(initialNowMs: number, serverUtcOffsetHours: number): TrackerClock {
  const [nowMs, setNowMs] = useState(initialNowMs)
  const [utcOffsetHours, setUtcOffsetHours] = useState(serverUtcOffsetHours)
  const [timeZoneName, setTimeZoneName] = useState<string | null>(null)

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    setUtcOffsetHours(decemberUtcOffsetHours(new Date().getUTCFullYear()))
    setTimeZoneName(resolveTimeZoneName())
    setNowMs(Date.now())

    const interval = setInterval(() => setNowMs(Date.now()), 1000)
    return () => clearInterval(interval)
  }, [])
  /* eslint-enable react-hooks/set-state-in-effect */

  return { nowMs, utcOffsetHours, timeZoneName }
}
